import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API, formatPrice, productImg } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Loader, EmptyState } from "@/components/States";
import { Minus, Plus, ChevronLeft, ShoppingBag } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { settings } = useSettings();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [variantIdx, setVariantIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [customization, setCustomization] = useState("");

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setProduct(false))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Loader />;
  if (!product)
    return (
      <EmptyState
        title="Product not found"
        action={
          <Link to="/shop" className="rounded-full bg-brand px-6 py-3 font-medium text-white">
            Back to Shop
          </Link>
        }
      />
    );

  const variant = product.variants[variantIdx];
  const stockManaged = variant && typeof variant.stock === "number";
  const stock = stockManaged ? variant.stock : Infinity;
  const outOfStock = stockManaged && stock <= 0;
  const maxQty = stockManaged ? stock : Infinity;

  const handleAdd = () => {
    if (outOfStock) return;
    if (product.customizable && product.customization_required !== false && !customization.trim()) return;
    addItem({
      product_id: product.id,
      name: product.name,
      image: productImg(product),
      variant_label: variant.label,
      price: variant.price,
      quantity: Math.min(qty, maxQty),
      stock: stockManaged ? stock : null,
      customization: product.customizable ? customization.trim() : "",
      customization_label: product.customizable ? (product.customization_label || "Personalisation") : "",
    });
  };

  const waMsg = `Hi ${settings.business_name || "Taanya Homefoods"}, I'd like to enquire about *${product.name}* (${variant.label} - ${formatPrice(variant.price)}).`;

  return (
    <div className="mx-auto max-w-6xl animate-fade-in px-4 py-10 sm:px-6 lg:px-8">
      <button
        data-testid="back-btn"
        onClick={() => navigate(-1)}
        className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-brand-muted hover:text-brand"
      >
        <ChevronLeft className="h-4 w-4" /> Back
      </button>

      <div className="grid gap-10 md:grid-cols-2">
        <div className="overflow-hidden rounded-3xl border border-brand-line bg-cream">
          <img src={productImg(product)} alt={product.name} className="max-h-[70vh] w-full object-cover" />
        </div>

        <div>
          {product.bestseller && (
            <span className="mb-3 inline-block rounded-full bg-brand-gold px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-dark">
              Top Selling
            </span>
          )}
          <h1 data-testid="product-name" className="font-heading text-4xl text-brand-dark">
            {product.name}
          </h1>
          {product.description && <p className="mt-3 text-brand-muted">{product.description}</p>}

          <p data-testid="product-price" className="mt-5 font-heading text-4xl text-brand">
            {formatPrice(variant.price)}
          </p>
          {stockManaged && (
            <p
              data-testid="stock-indicator"
              className={`mt-2 text-sm font-medium ${outOfStock ? "text-red-600" : stock <= 5 ? "text-brand" : "text-brand-green"}`}
            >
              {outOfStock ? "Out of Stock" : stock <= 5 ? `Only ${stock} left in stock` : "In Stock"}
            </p>
          )}

          {/* Variants */}
          {product.variants.length > 1 && (
            <div className="mt-6">
              <p className="mb-2 text-sm font-medium text-brand-muted">Choose size</p>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v, i) => (
                  <button
                    key={v.label}
                    data-testid={`variant-${i}`}
                    onClick={() => { setVariantIdx(i); setQty(1); }}
                    className={`rounded-full border px-5 py-2 text-sm font-medium transition-colors ${
                      i === variantIdx ? "border-brand bg-brand text-white" : "border-brand-line bg-white text-brand-dark hover:bg-brand-gold-light"
                    }`}
                  >
                    {v.label} — {formatPrice(v.price)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Customization */}
          {product.customizable && (
            <div className="mt-6">
              <label className="mb-1.5 block text-sm font-medium text-brand-muted">
                {product.customization_label || "Personalisation"}{" "}
                {product.customization_required !== false && <span className="text-brand">*</span>}
              </label>
              <input
                data-testid="customization-input"
                value={customization}
                onChange={(e) => setCustomization(e.target.value)}
                placeholder={`e.g. ${product.customization_label || "Happy Birthday Aarav"}`}
                className="w-full rounded-xl border border-brand-line bg-white px-4 py-3 text-brand-dark outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-brand-gold"
              />
            </div>
          )}

          {/* Quantity */}
          <div className="mt-6">
            <p className="mb-2 text-sm font-medium text-brand-muted">Quantity</p>
            <div className="inline-flex items-center rounded-full border border-brand-line bg-white">
              <button data-testid="qty-decrease" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 text-brand-dark">
                <Minus className="h-4 w-4" />
              </button>
              <span data-testid="qty-value" className="w-10 text-center font-semibold">
                {qty}
              </span>
              <button data-testid="qty-increase" onClick={() => setQty(Math.min(maxQty, qty + 1))} disabled={qty >= maxQty} className="p-3 text-brand-dark disabled:opacity-40">
                <Plus className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button
              data-testid="add-to-cart-btn"
              onClick={handleAdd}
              disabled={outOfStock || (product.customizable && product.customization_required !== false && !customization.trim())}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-8 py-3 font-medium text-white shadow-sm transition-transform hover:-translate-y-0.5 hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ShoppingBag className="h-5 w-5" /> {outOfStock ? "Out of Stock" : "Add to Cart"}
            </button>
            <WhatsAppButton testId="product-whatsapp-btn" variant="outline" label="Enquire on WhatsApp" message={waMsg} />
          </div>
          {product.customizable && product.customization_required !== false && !customization.trim() && (
            <p className="mt-2 text-sm text-brand">Please fill in the {(product.customization_label || "personalisation").toLowerCase()} to continue.</p>
          )}

          {/* Additional information */}
          {(product.ingredients || product.weight || product.quantity_info || product.shelf_life || product.storage_instructions || product.other_info) && (
            <div data-testid="product-additional-info" className="mt-8 rounded-2xl border border-brand-line bg-cream p-5">
              <h3 className="font-heading text-xl text-brand-dark">Additional Information</h3>
              <dl className="mt-3 space-y-2 text-sm">
                {[
                  ["Ingredients", product.ingredients],
                  ["Weight", product.weight],
                  ["Quantity", product.quantity_info],
                  ["Shelf Life", product.shelf_life],
                  ["Storage", product.storage_instructions],
                  ["More Info", product.other_info],
                ]
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <div key={k} className="flex gap-2">
                      <dt className="w-28 flex-shrink-0 font-medium text-brand-muted">{k}</dt>
                      <dd className="text-brand-dark">{v}</dd>
                    </div>
                  ))}
              </dl>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
